import React, { useState, useEffect } from 'react';
import type { RootState } from '../../store/store';
import {
  deleteUser,
  updateUser,
  getMobileAppInfo,
  getBatchedUsers,
  updateUserAmbassadorStatus,
} from '../../services/users';
import { User, UserRole } from '../../types/User';
import Header from '../../components/Global/Header';
import { Crosshair, Trash, User as UserIcon, Gift, Wind } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { HeaderType } from '../../types/Header';
import Switch from '@mui/material/Switch';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { getUserInfo, impersonateUser } from '../../services/auth';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { authSuccess } from '../../slices/auth/authSlice';
import SchoolDashboard from '../../components/Admin/SchoolDashboard';
import AdminCounters from '../../components/Admin/AdminCounters';
import AdvocateDashboard from '../../components/Admin/AdvocateDashboard';
import GroupDashboard from '../../components/Admin/GroupDashboard';
import { fetchExams } from '../../services/exams';
import CustomModal from '../../components/Global/CustomModal';

const Admin: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.user.profile);

  const [adminView, setAdminView] = useState<
    'users' | 'ambassadors' | 'schools' | 'groups'
  >('users');

  const [users, setUsers] = useState<User[]>([]);
  const [hideSynaptiq, setHideSynaptiq] = useState(true);

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [showUpdateRoleModal, setShowUpdateRoleModal] = useState(false);

  const [pendingModifyUserId, setPendingModifyUserId] = useState<
    string | null
  >();

  const [pendingUpdateRoleSelection, setPendingUpdateRoleSelection] =
    useState<UserRole | null>(null);
  const [mobileAppInfo, setMobileAppInfo] = useState<{
    iosUsers: number;
    androidUsers: number;
  }>();

  const userRoles = Object.values(UserRole);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [currentUserSkip, setCurrentUserSkip] = useState(0);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [totalUserCount, setTotalUserCount] = useState(0);

  const [userSearch, setUserSearch] = useState('');

  useEffect(() => {
    fetchPaginatedUsers();
    fetchMobileAppInfo();
  }, []);

  useEffect(() => {
    fetchPaginatedUsers();
    fetchMobileAppInfo();
  }, [hideSynaptiq]);

  const fetchPaginatedUsers = async (skip: number = 0) => {
    try {
      const response = await getBatchedUsers(
        skip,
        100,
        hideSynaptiq,
        userSearch
      );
      const fetchedUsers = response.users;

      if (skip === 0) {
        // Reset the list for first page
        setUsers(fetchedUsers);
      } else {
        // For subsequent pages, append without duplicates
        setUsers((prevUsers) => {
          // Get unique IDs to avoid duplicates
          const existingIds = new Set(prevUsers.map((user) => user.id));
          const newUsers = fetchedUsers.filter(
            (user: User) => !existingIds.has(user.id)
          );
          return [...prevUsers, ...newUsers];
        });
      }

      setHasMoreUsers(response.hasMore);
      setTotalUserCount(
        hideSynaptiq ? response.nonSynaptiqCount : response.totalCount
      );
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchMobileAppInfo = async () => {
    const mobileAppInfo = await getMobileAppInfo();
    setMobileAppInfo(mobileAppInfo);
  };

  const initiateDeleteRequest = (userId: string) => {
    setPendingModifyUserId(userId);
    setShowDeleteModal(true);
  };

  const handleDeleteUser = async () => {
    if (!pendingModifyUserId) return;
    await deleteUser(pendingModifyUserId);

    // Update local state by removing the deleted user
    setUsers((prevUsers) =>
      prevUsers.filter((user) => user.id !== pendingModifyUserId)
    );

    // Also refresh from server to ensure data consistency
    fetchPaginatedUsers(currentUserSkip);

    setPendingModifyUserId(null);
    hideDeleteModal();
  };

  const hideDeleteModal = () => {
    setShowDeleteModal(false);
    setPendingModifyUserId(null);
  };

  const initiateUpdateRole = (userId: string) => {
    setPendingModifyUserId(userId);
    setShowUpdateRoleModal(true);
  };

  const impersonate = async (userId: string) => {
    try {
      const response = await impersonateUser(userId);
      if (
        response &&
        response.token &&
        response.refreshToken &&
        response.userId
      ) {
        dispatch(
          authSuccess({
            authToken: response.token,
            refreshToken: response.refreshToken,
            userId: response.userId,
          })
        );
        await getUserInfo();
        await fetchExams();
        navigate('/');
      }
    } catch (error) {
      console.error('Error impersonating user:', error);
    }
  };

  const hideUpdateRoleModal = () => {
    setShowUpdateRoleModal(false);
    setPendingUpdateRoleSelection(null);
    setPendingModifyUserId(null);
  };

  const toggleApprovedReferrer = async (userId: string) => {
    if (!userId) return;
    const currentApprovedReferrerStatus = users.find(
      (user) => user.id === userId
    )?.isApprovedReferrer;
    await updateUser(userId, {
      isApprovedReferrer: !currentApprovedReferrerStatus,
    });
    enqueueSnackbar(
      `Status set to ${!currentApprovedReferrerStatus ? 'approved' : 'not approved'}`,
      { autoHideDuration: 3000 }
    );
    fetchPaginatedUsers();
  };

  const addOrResetAmbassador = async (
    userId: string,
    type: 'reset' | 'add'
  ) => {
    if (!userId) return;
    await updateUserAmbassadorStatus(userId, true, moment().toISOString());
    enqueueSnackbar(
      type === 'reset' ? 'Ambassador start date reset.' : 'Ambassador added.',
      { autoHideDuration: 3000 }
    );
    fetchPaginatedUsers();
  };

  const handleUpdateRole = async () => {
    if (!pendingModifyUserId || !pendingUpdateRoleSelection) return;
    await updateUser(pendingModifyUserId, {
      role: pendingUpdateRoleSelection,
    });
    hideUpdateRoleModal();
    fetchPaginatedUsers();
  };

  const loadMoreUsers = () => {
    const nextSkip = currentUserSkip + 100;
    setCurrentUserSkip(nextSkip);
    fetchPaginatedUsers(nextSkip);
  };

  // Add debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentUserSkip(0); // Reset pagination when searching
      fetchPaginatedUsers(0);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [userSearch]);

  return (
    <div className="admin">
      <div className="admin_body">
        <Header type={HeaderType.default} />
        <div className="admin_body_content">
          <div
            style={{
              display: 'flex',
              alignContent: 'center',
              justifyContent: 'space-between',
            }}
          >
            <h1 className="m-t-0 m-b-1">Admin Dashboard</h1>
            <div>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hideSynaptiq}
                      onChange={() => setHideSynaptiq(!hideSynaptiq)}
                    />
                  }
                  label="Hide Synaptiq Users"
                />
              </FormGroup>
            </div>
          </div>
          <AdminCounters
            totalUserCount={totalUserCount}
            mobileAppInfo={mobileAppInfo || { iosUsers: 0, androidUsers: 0 }}
          />
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'space-between',
            }}
          >
            <div>
              {adminView === 'users' && (
                <input
                  id="user-search"
                  value={userSearch}
                  placeholder="Search..."
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUserSearch(event.target.value);
                  }}
                  style={{
                    border: '1px solid black',
                    borderRadius: '4px',
                    padding: '0.5rem',
                  }}
                />
              )}
            </div>
            <ToggleButtonGroup
              value={adminView}
              exclusive
              onChange={(_, value) => setAdminView(value)}
            >
              <ToggleButton value="users">Users</ToggleButton>
              <ToggleButton value="ambassadors">Advocates</ToggleButton>
              <ToggleButton value="schools">Schools</ToggleButton>
              <ToggleButton value="groups">Groups</ToggleButton>
            </ToggleButtonGroup>
          </div>
          {adminView === 'users' && (
            <>
              <table className="users-list">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>ID</th>
                    <th>OAuth</th>
                    <th>Role</th>
                    <th style={{ width: '175px' }}>Approved Referrer</th>
                    <th>Created At</th>
                    {currentUser?.role === 'admin' && (
                      <th style={{ width: '130px' }}>Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {users?.map((user: User) => (
                    <tr className="user-row" key={user.id}>
                      <td>
                        {user.name ? user.name : 'N/A'}
                        {currentUser?.id === user.id ? ' (You)' : ''}
                      </td>
                      <td>{user.email}</td>
                      <td>{user.id}</td>
                      <td>{user.oauth ? user.oauthStrategy : 'Not OAuth'}</td>
                      <td>{user.role}</td>
                      <td>{user.isApprovedReferrer ? 'Yes' : 'No'}</td>
                      <td>{moment(user.createdAt).format('MM/DD/YYYY')}</td>
                      {currentUser?.role === 'admin' && (
                        <td>
                          <Tooltip
                            title={'Impersonate User'}
                            enterTouchDelay={0}
                          >
                            <button
                              className="button button--icon-only"
                              style={{ display: 'inline-flex' }}
                              onClick={() => impersonate(user.id)}
                            >
                              <Crosshair />
                            </button>
                          </Tooltip>
                          <Tooltip
                            title={'Update User Role'}
                            enterTouchDelay={0}
                          >
                            <button
                              className="button button--icon-only"
                              style={{ display: 'inline-flex' }}
                              onClick={() => initiateUpdateRole(user.id)}
                            >
                              <UserIcon />
                            </button>
                          </Tooltip>
                          <Tooltip title={'Delete User'} enterTouchDelay={0}>
                            <button
                              className="button button--icon-only"
                              style={{ display: 'inline-flex' }}
                              onClick={() => initiateDeleteRequest(user.id)}
                            >
                              <Trash />
                            </button>
                          </Tooltip>
                          <Tooltip
                            title={'Toggle Approved Referrer'}
                            enterTouchDelay={0}
                          >
                            <button
                              className="button button--icon-only"
                              style={{ display: 'inline-flex' }}
                              onClick={() => toggleApprovedReferrer(user.id)}
                            >
                              <Gift />
                            </button>
                          </Tooltip>
                          <Tooltip title={'Add Ambassador'} enterTouchDelay={0}>
                            <button
                              className="button button--icon-only"
                              style={{ display: 'inline-flex' }}
                              onClick={() =>
                                addOrResetAmbassador(user.id, 'add')
                              }
                            >
                              <Wind />
                            </button>
                          </Tooltip>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {userSearch === '' && (
                <div
                  className="pagination-controls"
                  style={{ marginTop: '1rem', textAlign: 'center' }}
                >
                  <div className="helper" style={{ marginBottom: '0.5rem' }}>
                    Showing {users.length} of {totalUserCount} users
                  </div>
                  {hasMoreUsers && (
                    <button className="button" onClick={loadMoreUsers}>
                      Load More Users
                    </button>
                  )}
                </div>
              )}
            </>
          )}
          {adminView === 'ambassadors' && (
            <AdvocateDashboard hideSynaptiq={hideSynaptiq} />
          )}
          {adminView === 'schools' && <SchoolDashboard />}
          {adminView === 'groups' && <GroupDashboard />}
        </div>
      </div>
      <CustomModal
        open={showDeleteModal}
        onClose={hideDeleteModal}
        name="delete-user"
        glass={true}
      >
        <div className="modal modal--delete-user">
          <div className="modal_header">
            <h2>Delete User</h2>
          </div>
          <div className="modal_content">
            <p>Are you sure you want to delete this user?</p>
            <p>{pendingModifyUserId}</p>
          </div>
          <div className="modal_actions">
            <button className="button button--error" onClick={handleDeleteUser}>
              Delete
            </button>
          </div>
        </div>
      </CustomModal>
      <CustomModal
        open={showUpdateRoleModal}
        onClose={hideUpdateRoleModal}
        name="update-role"
        glass={true}
      >
        <div className="modal modal--update-role">
          <div className="modal_header">
            <h2>Update User Role</h2>
          </div>
          <div className="modal_content">
            <select
              value={pendingUpdateRoleSelection ?? ''}
              onChange={(e) =>
                setPendingUpdateRoleSelection(e.target.value as UserRole)
              }
            >
              <option value="">Select a role</option>
              {userRoles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </div>
          <div className="modal_actions">
            <button className="button" onClick={handleUpdateRole}>
              Save
            </button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default Admin;
